<template>
    <div>
        <h2>Update your account</h2>

        <form @submit.prevent="submit" ref="form" action="account/" data-method="put">
            <fieldset>
                <div class="form-group" v-bind:class="{'error': errors.hard_limit}" v-if="account.plan">
                    <label for="settings-edit-hard_limit">Overage limit (in total number of credits)</label>
                    <input type="number" name="name" v-model="form.hard_limit" id="settings-edit-hard_limit" placeholder="Overage limit" />
                    <p v-if="errors.hard_limit" class="error">{{ errors.hard_limit }}</p>
                    <p class="help">
                        Allow you to convert more than the limit of your current credits's plan. Once we reach that limit, we will stop converting further documents.<br />
                        A dedicated invoice will be generated for your extra usage.
                    </p>
                </div>
            </fieldset>
            <fieldset>
                <div class="row">
                    <div class="form-group col-6" v-bind:class="{'error': errors.name}">
                        <label for="settings-edit-name">Full name</label>
                        <input type="text" name="name" v-model="form.name" id="settings-edit-name" placeholder="Your full name" />
                        <p v-if="errors.name" class="error">{{ errors.name }}</p>
                    </div>
                    <div class="form-group col-6" v-bind:class="{'error': errors.email}">
                        <label for="settings-edit-email">Email</label>
                        <input type="email" name="email" v-model="form.email" id="settings-edit-email" placeholder="Your email" required />
                        <p v-if="errors.email" class="error">{{ errors.email }}</p>
                    </div>
                </div>
                <div class="row">
                    <div class="form-group col-6" v-bind:class="{'error': errors.company_name}">
                        <label for="settings-edit-company_name">Company name</label>
                        <input type="text" name="name" v-model="form.company_name" id="settings-edit-company_name" placeholder="Company name" />
                        <p v-if="errors.company_name" class="error">{{ errors.company_name }}</p>
                    </div>
                    <div class="form-group col-6" v-bind:class="{'error': errors.company_vat}">
                        <label for="settings-edit-company_vat">EU VAT number</label>
                        <input type="text" placeholder="Your European VAT number " name="company_vat" v-model="form.company_vat"  id="settings-edit-company_vat" :disabled="!isVatAllowed()" />
                        <p v-if="errors.company_vat" class="error">{{ errors.company_vat }}</p>
                    </div>
                </div>
                <div class="form-group" v-bind:class="{'error': errors.company_details}">
                    <label for="settings-edit-company_details">Company address</label>
                    <textarea name="company_details" rows="4" cols="50" id="settings-edit-company_details" placeholder="Your company address, city and zipcode." autocomplete="off" v-model="form.company_details"></textarea>
                    <p v-if="errors.company_details" class="error">{{ errors.company_details }}</p>
                </div>
                <div class="row">
                    <div class="form-group col-6" v-bind:class="{'error': errors.country}">
                        <label for="settings-edit-country">Country</label>
                        <select id="settings-edit-country" name="country" placeholder="Your country" required v-model="form.country">
                            <optgroup>
                                <option value="US">United States</option>
                                <option value="CA">Canada</option>
                                <option value="GB">United Kingdom</option>
                                <option value="FR">France</option>
                                <option value="DE">Germany</option>
                            </optgroup>
                            <optgroup label="-----------">
                                <option v-for="country in countries" v-bind:key="country.code" :value="country.code">{{ country.value }}</option>
                            </optgroup>
                        </select>
                        <p v-if="errors.country" class="error">{{ errors.country }}</p>
                    </div>
                    <div class="form-group col-6" v-bind:class="{'error': errors.billing_email}">
                        <label for="settings-edit-billing_email">Billing email (if different)</label>
                        <input type="email" name="billing_email" v-model="form.billing_email" id="settings-edit-billing_email" placeholder="Your billing email" />
                        <p v-if="errors.billing_email" class="error">{{ errors.billing_email }}</p>
                    </div>
                </div>
            </fieldset>
            <fieldset>
                <h3>Change your password</h3>
                <div class="row">
                    <div class="form-group col-6" v-bind:class="{'error': errors.password}">
                        <label for="settings-edit-password">New password</label>
                        <password-input name="password" v-model="form.password" id="settings-edit-password" placeholder="Your new password" />
                        <p v-if="errors.password" class="error">{{ errors.password }}</p>
                    </div>
                    <div class="form-group col-6" v-bind:class="{'error': errors.confirm}">
                        <label for="settings-edit-confirm">Confirm your new password</label>
                        <password-input name="confirm" v-model="form.confirm" id="settings-edit-confirm" placeholder="Confirm your new password" />
                        <p v-if="errors.confirm" class="error">{{ errors.confirm }}</p>
                    </div>
                </div>
            </fieldset>
            <div class="submit">
                <button-loader :disabled="disabled" :sending="sending" label="Update your details" />
                <a href="javascript:;" title="Click here to delete your account" @click="confirmDelete()" class="delete">Delete my account</a>
            </div>
        </form>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { countries, vatCountries } from '@/assets/countries'
import VueForm from '@/plugins/VueForm'
import ButtonLoader from '@/components/ButtonLoader'
import PasswordInput from '@/components/PasswordInput'

export default {
    components: { ButtonLoader, PasswordInput },
    mixins: [VueForm],
    data () {
        return {
            form: {
                name: null,
                email: null,
                company_name: null,
                company_details: null,
                company_vat: null,
                country: null,
                billing_email: null,
                password: null,
                confirm: null,
                hard_limit: null
            },
            countries: countries
        }
    },
    computed: {
        ...mapState({
            account: state => state
        })
    },
    watch: {
        account (to, from) {
            this.loadStore(to)
        }
    },
    created () {
        if (this.account) {
            this.loadStore(this.account)
        }
    },
    methods: {
        loadStore (val) {
            if (typeof val !== 'object') {
                return
            }

            Object.keys(this.form).forEach(k => {
                if (k in val) {
                    this.form[k] = val[k]
                }
            })
        },
        processSubmit (body) {
            this.$store.commit('set', body.account)
            this.form.password = null
            this.form.confirm = null
            this.$toast.success('Your account information has been successfully updated.')
        },
        confirmDelete () {
            if (confirm('Are you sure you want to delete your account?\nThis will remove all your history, conversions, and downgrade you to the free plan.')) {
                this.$http.delete('account/').then(response => {
                    this.$storage.remove('sessionId')
                    this.$router.push({ name: 'login', query: { deleted: true } })
                })
            }
        },
        isVatAllowed () {
            return vatCountries.indexOf(this.form.country) > -1
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../public/css/vars';

    form {
        fieldset {
            background-color: #fff;
            border-radius: 8px;
            padding: 20px;

            input, textarea, select {
                border-color: $menu-right-border;
                color: $link-color-secondary;

                &::placeholder {
                    color: fade-out($link-color-secondary, 0.5)
                }

                &[disabled] {
                    &[disabled] {
                        background-color: #eee;
                        color: #999;
                        &::placeholder { color: #999 }
                    }
                }
            }

            h3 {
                padding: 0;
                margin: 10px 0 20px;
                font-size: 2.8rem;
            }

            p.error + p.help {
                margin-top: 10px;
            }

            p.help {
                font-size: .9em;
                color: #666666;
                font-style: italic;
                margin-bottom: 10px;
                margin-left: 10px;
            }
        }

        p.error { margin-left: 10px }

        button {
            margin-top: 20px;
            padding: 15px 50px;
        }

        .submit {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .delete {
            color: $form-field-error-color;
            border-color: transparent;

            &:hover {
                color: $danger-color;
                border-color: $danger-color;
            }
        }
    }
</style>
